import { useState, useEffect, useCallback } from "react";
import { Phone, Mail, Calendar, Clock, MessageSquare, User, RefreshCw, ArrowLeft, CheckCircle, XCircle, Trash2, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/bookings`);
      if (!response.ok) throw new Error('Failed to fetch bookings');
      const data = await response.json();
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setBookings(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (bookingId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (!response.ok) throw new Error('Failed to update');
      const data = await response.json();
      if (data.success) {
        setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b));
        toast.success(`Booking ${newStatus}!`);
      }
    } catch (err) {
      toast.error("Failed to update booking status");
    }
  };

  const deleteBooking = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete');
      const data = await response.json();
      if (data.success) {
        setBookings(prev => prev.filter(b => b.id !== bookingId));
        toast.success("Booking deleted");
        setDeleteConfirm(null);
      }
    } catch (err) {
      toast.error("Failed to delete booking");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    }
  };

  const filtered = filter === "all" ? bookings : bookings.filter(b => b.status === filter);
  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const confirmedCount = bookings.filter(b => b.status === 'confirmed').length;
  const cancelledCount = bookings.filter(b => b.status === 'cancelled').length;

  return (
    <div className="min-h-screen bg-[#0a0318]">
      <header className="bg-[#0a0318]/95 backdrop-blur-lg border-b border-amber-500/30 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Site</span>
              </Link>
              <div className="h-6 w-px bg-amber-500/30"></div>
              <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
            </div>
            <Button onClick={fetchBookings} disabled={loading} className="bg-amber-600 hover:bg-amber-700" data-testid="refresh-bookings-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#110422]/50 border border-amber-500/20 rounded-xl p-5 cursor-pointer hover:border-amber-500/40 transition-colors" onClick={() => setFilter("all")} data-testid="filter-all">
            <p className="text-white/70 text-sm mb-1">Total</p>
            <p className="text-3xl font-bold text-white">{bookings.length}</p>
          </div>
          <div className="bg-[#110422]/50 border border-amber-500/20 rounded-xl p-5 cursor-pointer hover:border-amber-500/40 transition-colors" onClick={() => setFilter("pending")} data-testid="filter-pending">
            <p className="text-white/70 text-sm mb-1">Pending</p>
            <p className="text-3xl font-bold text-amber-400">{pendingCount}</p>
          </div>
          <div className="bg-[#110422]/50 border border-green-500/20 rounded-xl p-5 cursor-pointer hover:border-green-500/40 transition-colors" onClick={() => setFilter("confirmed")} data-testid="filter-confirmed">
            <p className="text-white/70 text-sm mb-1">Confirmed</p>
            <p className="text-3xl font-bold text-green-400">{confirmedCount}</p>
          </div>
          <div className="bg-[#110422]/50 border border-red-500/20 rounded-xl p-5 cursor-pointer hover:border-red-500/40 transition-colors" onClick={() => setFilter("cancelled")} data-testid="filter-cancelled">
            <p className="text-white/70 text-sm mb-1">Cancelled</p>
            <p className="text-3xl font-bold text-red-400">{cancelledCount}</p>
          </div>
        </div>

        {/* Filter indicator */}
        {filter !== "all" && (
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-amber-400" />
            <span className="text-white/70 text-sm">Showing: <span className="text-amber-400 font-semibold capitalize">{filter}</span></span>
            <button onClick={() => setFilter("all")} className="text-amber-400 hover:text-amber-300 text-sm underline ml-2">Show All</button>
          </div>
        )}

        {/* Bookings List */}
        <div className="bg-[#110422]/50 border border-amber-500/20 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-amber-500/20">
            <h2 className="text-xl font-bold text-white">Customer Bookings</h2>
            <p className="text-white/70 text-sm">Manage, confirm, cancel, or delete booking requests</p>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 text-amber-400 animate-spin mx-auto mb-4" />
              <p className="text-white/70">Loading bookings...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <Button onClick={fetchBookings} variant="outline">Try Again</Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-white/70">{filter === "all" ? "No bookings yet." : `No ${filter} bookings.`}</p>
            </div>
          ) : (
            <div className="divide-y divide-amber-500/10">
              {filtered.map((booking) => (
                <div key={booking.id} className="p-6 hover:bg-amber-500/5 transition-colors" data-testid={`booking-${booking.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-600 to-amber-400 flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{booking.name}</h3>
                          <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${getStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-white/70">
                          <Mail className="w-4 h-4 text-amber-400" />
                          <a href={`mailto:${booking.email}`} className="hover:text-amber-300">{booking.email}</a>
                        </div>
                        <div className="flex items-center gap-2 text-white/70">
                          <Phone className="w-4 h-4 text-amber-400" />
                          <a href={`tel:${booking.phone}`} className="hover:text-amber-300">{booking.phone}</a>
                        </div>
                        <div className="flex items-center gap-2 text-white/70">
                          <Calendar className="w-4 h-4 text-amber-400" />
                          <span>{booking.date || 'Date not specified'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/70">
                          <Clock className="w-4 h-4 text-amber-400" />
                          <span>{booking.time || 'Time not specified'}</span>
                        </div>
                      </div>
                      {booking.message && (
                        <div className="mt-3 flex items-start gap-2 text-white/70 text-sm">
                          <MessageSquare className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                          <p className="italic">"{booking.message}"</p>
                        </div>
                      )}
                    </div>

                    <div className="lg:text-right space-y-3">
                      <div className="inline-block bg-amber-500/20 text-amber-300 px-3 py-1 rounded-full text-sm font-medium">
                        {booking.service}
                      </div>

                      {/* Contact buttons */}
                      <div className="flex gap-2 lg:justify-end">
                        <a href={`tel:${booking.phone}`} className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">
                          <Phone className="w-3 h-3" /> Call
                        </a>
                        <a href={`mailto:${booking.email}`} className="inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">
                          <Mail className="w-3 h-3" /> Email
                        </a>
                      </div>

                      {/* Status management buttons */}
                      <div className="flex gap-2 lg:justify-end">
                        {booking.status !== 'confirmed' && (
                          <button
                            onClick={() => updateStatus(booking.id, 'confirmed')}
                            className="inline-flex items-center gap-1 bg-green-600/20 border border-green-500/30 hover:bg-green-600/40 text-green-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                            data-testid={`confirm-${booking.id}`}
                          >
                            <CheckCircle className="w-3 h-3" /> Confirm
                          </button>
                        )}
                        {booking.status !== 'cancelled' && (
                          <button
                            onClick={() => updateStatus(booking.id, 'cancelled')}
                            className="inline-flex items-center gap-1 bg-red-600/20 border border-red-500/30 hover:bg-red-600/40 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                            data-testid={`cancel-${booking.id}`}
                          >
                            <XCircle className="w-3 h-3" /> Cancel
                          </button>
                        )}
                        {booking.status !== 'pending' && (
                          <button
                            onClick={() => updateStatus(booking.id, 'pending')}
                            className="inline-flex items-center gap-1 bg-amber-600/20 border border-amber-500/30 hover:bg-amber-600/40 text-amber-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                          >
                            Reopen
                          </button>
                        )}
                        {deleteConfirm === booking.id ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => deleteBooking(booking.id)}
                              className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                              data-testid={`delete-confirm-${booking.id}`}
                            >
                              Yes, Delete
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="inline-flex items-center gap-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(booking.id)}
                            className="inline-flex items-center gap-1 bg-gray-600/20 border border-gray-500/30 hover:bg-red-600/20 hover:border-red-500/30 hover:text-red-400 text-gray-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                            data-testid={`delete-${booking.id}`}
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Admin;
